import {
  type MsGraphRequest,
  type MsGraphResponse,
  type MsGraphStatus
} from '../types';

import { type IframeAuth } from './iframe-auth';

/** Extra per-call options for the ergonomic Graph helpers. */
export interface MsGraphRequestOptions {
  /** Extra request headers (the host may filter these). */
  headers?: Record<string, string>;
  /** Optional scopes hint for the token the host should acquire. */
  scopes?: string[];
}

/**
 * A client for the host's Microsoft Graph (MSAL) session, usable from an
 * embedded app. Every call is **proxied** through the host: the host acquires
 * the token via MSAL, performs the Graph request, and returns only the
 * response. The MSAL access token never crosses the frame boundary.
 *
 * Obtain one via `useDocyrusMsGraph()` (React), `getDocyrusAuth().msGraph`
 * (Vue), or `AuthManager.getMsGraphClient()`. Only available in iframe/WebView
 * mode — it is `null` elsewhere.
 */
export class MsGraphClient {
  private iframeAuth: IframeAuth;
  constructor(iframeAuth: IframeAuth) {
    this.iframeAuth = iframeAuth;
  }
  /** Whether the host's MSAL session is signed in, plus optional account info. */
  status(): Promise<MsGraphStatus> {
    return this.iframeAuth.requestMsGraphStatus();
  }
  /** Convenience: resolves to whether the host's MSAL session is signed in. */
  async isSignedIn(): Promise<boolean> {
    const status = await this.iframeAuth.requestMsGraphStatus();

    return status.signedIn;
  }
  /** Perform an arbitrary Microsoft Graph request via the host's MSAL session. */
  request(request: MsGraphRequest): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph(request);
  }
  /** GET a Graph resource, e.g. `get('/me/messages?$top=5')`. */
  get(path: string, options?: MsGraphRequestOptions): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({ method: 'GET', path, ...options });
  }
  /** POST a JSON body to a Graph resource. */
  post(path: string, body?: unknown, options?: MsGraphRequestOptions): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({
      method: 'POST', path, body, ...options
    });
  }
  /** PATCH a JSON body to a Graph resource. */
  patch(path: string, body?: unknown, options?: MsGraphRequestOptions): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({
      method: 'PATCH', path, body, ...options
    });
  }
  /** PUT a JSON body to a Graph resource. */
  put(path: string, body?: unknown, options?: MsGraphRequestOptions): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({
      method: 'PUT', path, body, ...options
    });
  }
  /** DELETE a Graph resource. */
  delete(path: string, options?: MsGraphRequestOptions): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({ method: 'DELETE', path, ...options });
  }
  /** Convenience: GET `/me` — the signed-in user's profile. */
  getMe(): Promise<MsGraphResponse> {
    return this.iframeAuth.requestMsGraph({ method: 'GET', path: '/me' });
  }
}
